import { useState } from 'react';

const businessHours = [
  { day: 'Monday', hours: '9\u2009am \u2013 5\u2009pm' },
  { day: 'Tuesday', hours: '9\u2009am \u2013 5\u2009pm' },
  { day: 'Wednesday', hours: '9\u2009am \u2013 5\u2009pm' },
  { day: 'Thursday', hours: '9\u2009am \u2013 5\u2009pm' },
  { day: 'Friday', hours: '9\u2009am \u2013 5\u2009pm' },
  { day: 'Saturday', hours: 'Closed' },
  { day: 'Sunday', hours: 'Closed' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company_alt: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '', company_alt: '' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const payload = new FormData(form);
      payload.delete('company_alt');

      const response = await fetch('/api/form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '', company_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Something went wrong. Please try again.';
        setErrorMessage(serverMsg);
        setSubmitStatus('error');
      }
    } catch {
      setErrorMessage('Network error. Please check your connection and try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayHours = businessHours.find((h) => h.day === today);

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="pt-32 pb-12 md:pt-40 md:pb-16 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <p className="font-mono text-xs text-stone-400 tracking-[0.25em] uppercase mb-5 whitespace-nowrap">
          Get in Touch
        </p>
        <h1 className="font-serif text-5xl md:text-7xl text-charcoal-900 mb-6">
          Start Your Project
        </h1>
        <p className="text-stone-500 text-base max-w-xl leading-relaxed">
          Whether you're scouting a site, preparing a submission, or ready to bring a project to market — we're here to help.
        </p>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5">
            <div className="space-y-10 mb-14">
              <div>
                <p className="font-mono text-[10px] text-stone-400 tracking-[0.2em] uppercase mb-2 whitespace-nowrap">Email</p>
                <a href="mailto:admin@udesignstudio.com.au" className="text-charcoal-800 text-lg hover:text-terracotta-500 transition-colors duration-300">
                  admin@udesignstudio.com.au
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] text-stone-400 tracking-[0.2em] uppercase mb-2 whitespace-nowrap">Phone</p>
                <a href="tel:+61390086321" className="text-charcoal-800 text-lg hover:text-terracotta-500 transition-colors duration-300">
                  (03) 9008 6321
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] text-stone-400 tracking-[0.2em] uppercase mb-2 whitespace-nowrap">Studio</p>
                <p className="text-charcoal-800 text-lg leading-relaxed">
                  Suite 218/89 High St<br />
                  Kew VIC 3101
                </p>
              </div>
            </div>

            <div className="border-t border-stone-200 pt-8">
              <p className="font-mono text-[10px] text-stone-400 tracking-[0.2em] uppercase mb-4 whitespace-nowrap">
                Business Hours
              </p>
              {todayHours && (
                <div className="mb-4 px-4 py-2.5 bg-terracotta-50/80 border border-terracotta-200/60 inline-block">
                  <p className="text-xs text-terracotta-700">
                    <span className="font-medium">Today ({todayHours.day})</span>: {todayHours.hours}
                  </p>
                </div>
              )}
              <ul className="space-y-2">
                {businessHours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between text-sm max-w-[260px]">
                    <span className={`${h.day === today ? 'text-terracotta-600 font-medium' : 'text-stone-500'}`}>
                      {h.day}
                    </span>
                    <span className={`${h.hours === 'Closed' ? 'text-stone-400 italic' : 'text-charcoal-700'} ${h.day === today ? 'font-medium' : ''}`}>
                      {h.hours}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-stone-500 tracking-wide mb-2 whitespace-nowrap">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-cream-50 border border-stone-300 text-charcoal-900 text-sm focus:outline-none focus:border-terracotta-400 transition-colors duration-300"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-stone-500 tracking-wide mb-2 whitespace-nowrap">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-cream-50 border border-stone-300 text-charcoal-900 text-sm focus:outline-none focus:border-terracotta-400 transition-colors duration-300"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-xs text-stone-500 tracking-wide mb-2 whitespace-nowrap">
                  Phone
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-cream-50 border border-stone-300 text-charcoal-900 text-sm focus:outline-none focus:border-terracotta-400 transition-colors duration-300"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs text-stone-500 tracking-wide mb-2 whitespace-nowrap">
                  Tell us about your project
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-cream-50 border border-stone-300 text-charcoal-900 text-sm focus:outline-none focus:border-terracotta-400 transition-colors duration-300 resize-none"
                ></textarea>
                <p className="text-[10px] text-stone-400 mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                value={formData.company_alt}
                onChange={handleChange}
                className="absolute opacity-0 pointer-events-none"
                style={{ position: 'absolute', left: '-9999px' }}
              />

              {submitStatus === 'success' && (
                <div className="p-4 bg-terracotta-50 border border-terracotta-200">
                  <p className="text-sm text-terracotta-800">Thank you for your message. We'll be in touch shortly.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{errorMessage || 'Something went wrong. Please try again.'}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-10 py-4 bg-charcoal-900 text-cream-50 text-sm font-medium tracking-wider uppercase hover:bg-charcoal-800 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
